import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useAction, useConvex } from 'convex/react';
import { api } from '@/lib/api';
import { toast } from '@/template/lib/toast';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ServiceStatusBadge } from '@/components/features/operator/service-status-badge';
import { AssignSubstituteDialog } from '@/components/features/operator/assign-substitute-dialog';
import { RepairPlatformInvoiceDialog, type PlatformInvoiceRepairTarget } from '@/components/features/operator/accounting/repair-platform-invoice-dialog';
import { ActivityReportEditCard } from '@/components/features/operator/activity-report-edit-card';
import { OperatorConfirmServiceCard } from '@/components/features/operator/operator-confirm-service-card';
import { OverrideApplicationCompensationDialog } from '@/components/features/operator/override-application-compensation-dialog';
import { DiensttauschFormularDialog, type OperatorDiensttauschFormularDraft } from '@/components/features/operator/diensttausch-formular-dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/check-box';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { Id } from 'convex/_generated/dataModel';
import type { TFunction } from 'i18next';
import { FEDERAL_STATES, KV_REGIONS_BY_STATE, SERVICE_TYPES, type FederalStateCode, type ServiceTypeValue } from '@/lib/constants/german_regions';
import { canOperatorEditApplicationCompensation } from '@/lib/operator-application-compensation';
import { canOperatorRemoveApplication } from '@/lib/operator-application-removal';
import { getServiceNotificationOutcome } from '@/lib/service-notification-result';
import { ConvexError } from 'convex/values';
import type { ServiceStatus } from '@/types/service-status';
import { type CompensationType } from '@/types/validators';
import { type OperatorServiceApplication, type OperatorServiceDuty, type OperatorServiceDetails } from '@/types/operator-service';
import { useTranslation } from 'react-i18next';
import { maxNotfallscheinbeteiligungRate } from 'convex/lib/notfallscheinbeteiligung';
import { berlinWallTimeToEpoch, epochToBerlinDateTimeLocalValue, formatBerlinDateTime } from '@/lib/berlin-time';

type ServiceType = ServiceTypeValue;

/**
 * Get service type label
 */
const getServiceTypeLabel = (type: ServiceType, t: (key: string) => string): string => {
  const serviceType = SERVICE_TYPES.find((st) => st.value === type);
  if (serviceType) {
    const key = `servicesList.serviceTypes.${serviceType.value}` as const;
    return t(key);
  }
  return type;
};

/**
 * Format date
 */
const formatDate = (timestamp: number, language: string): string => {
  return formatBerlinDateTime(timestamp, language.startsWith('en') ? 'en-US' : 'de-DE');
};

/**
 * Format currency
 */
const formatCurrency = (cents: number): string => {
  return `${(cents / 100).toFixed(2)}€`;
};

export const OperatorDiensttauschFormularCard = ({
  formular,
  draft,
  onOpen,
}: {
  formular: { generatedAt: number; pdfFileId?: Id<'_storage'> } | null | undefined;
  draft: OperatorDiensttauschFormularDraft | null | undefined;
  onOpen: (draft: OperatorDiensttauschFormularDraft, regenerate: boolean) => void;
}): React.JSX.Element => {
  const { t, i18n } = useTranslation('operator');
  const convex = useConvex();

  const download = async (): Promise<void> => {
    if (!formular?.pdfFileId) return;
    try {
      const url = await convex.query(api.app.storage.queries.getStorageUrl, { storageId: formular.pdfFileId });
      if (url) window.open(url, '_blank');
      else toast.error(t('serviceDetail.toasts.downloadUrlError'));
    } catch {
      toast.error(t('serviceDetail.toasts.downloadError'));
    }
  };

  return (
    <Card>
      <CardHeader className='flex flex-col items-stretch gap-4 space-y-0 sm:flex-row sm:items-center sm:justify-between'>
        <div className='space-y-1.5'>
          <CardTitle className='text-lg'>{t('serviceDetail.exchangeForm.title')}</CardTitle>
          <CardDescription>{t('serviceDetail.exchangeForm.description')}</CardDescription>
        </div>
        {!formular && (
          <Button variant='outline' size='sm' className='w-full sm:w-auto' onClick={() => draft && onOpen(draft, false)} disabled={!draft}>
            <Icons.Plus className='mr-2 size-4' />
            {t('serviceDetail.exchangeForm.create')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!formular ? (
          <div className='py-8 text-center'>
            <Icons.FileSignature className='size-8 text-muted-foreground mx-auto mb-2' />
            <p className='text-muted-foreground text-sm'>{t('serviceDetail.exchangeForm.empty')}</p>
            <p className='text-muted-foreground text-xs mt-1'>{t('serviceDetail.exchangeForm.emptyDescription')}</p>
          </div>
        ) : (
          <div className='flex flex-col items-stretch gap-4 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex items-center gap-4'>
              <Icons.FileSignature className='size-8 text-primary' />
              <div>
                <p className='font-medium'>{t('serviceDetail.exchangeForm.generatedTitle')}</p>
                <p className='text-sm text-muted-foreground'>{t('serviceDetail.exchangeForm.generatedAt', { date: formatDate(formular.generatedAt, i18n.language) })}</p>
              </div>
            </div>
            <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3'>
              <Badge variant='default' className='self-start sm:self-auto'>
                {t('serviceDetail.exchangeForm.generatedBadge')}
              </Badge>
              <Button variant='outline' size='sm' className='w-full sm:w-auto' onClick={() => draft && onOpen(draft, true)} disabled={!draft}>
                <Icons.RefreshCw className='mr-2 size-4' />
                {t('serviceDetail.exchangeForm.regenerate')}
              </Button>
              {formular.pdfFileId && (
                <Button variant='outline' size='sm' className='w-full sm:w-auto' onClick={download}>
                  <Icons.Download className='size-4 mr-2' />
                  {t('serviceDetail.exchangeForm.downloadPdf')}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const getStateCode = (state?: string): FederalStateCode | '' => {
  if (!state) return '';
  const normalized = state.trim().toLowerCase();
  const match = FEDERAL_STATES.find((entry) => entry.code.toLowerCase() === normalized || entry.name.toLowerCase() === normalized);
  return match?.code ?? '';
};

const getStateName = (stateCode?: FederalStateCode | ''): string | undefined => {
  if (!stateCode) return undefined;
  return FEDERAL_STATES.find((entry) => entry.code === stateCode)?.name;
};

/**
 * Loading skeleton
 */
const DetailsSkeleton = (): React.JSX.Element => (
  <div className='space-y-6'>
    <Skeleton className='h-8 w-64' />
    <div className='grid grid-cols-2 gap-4'>
      <Skeleton className='h-20' />
      <Skeleton className='h-20' />
      <Skeleton className='h-20' />
      <Skeleton className='h-20' />
    </div>
  </div>
);

/**
 * Status Change Dialog
 */
const StatusChangeDialog = ({ open, onOpenChange, currentStatus, serviceId }: { open: boolean; onOpenChange: (open: boolean) => void; currentStatus: ServiceStatus; serviceId: Id<'duties'> }) => {
  const { t } = useTranslation('operator');
  const [newStatus, setNewStatus] = useState<ServiceStatus>(currentStatus);
  const [reason, setReason] = useState('');
  const updateStatusMutation = useMutation(api.app.operatorServiceMutations.operatorUpdateServiceStatus);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error(t('serviceDetail.statusDialog.toasts.reasonRequired'));
      return;
    }

    try {
      await updateStatusMutation({
        serviceId,
        newStatus,
        reason,
      });
      toast.success(t('serviceDetail.statusDialog.toasts.success'));
      onOpenChange(false);
      setReason('');
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(t('serviceDetail.statusDialog.toasts.error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('serviceDetail.statusDialog.title')}</DialogTitle>
          <DialogDescription>{t('serviceDetail.statusDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div>
            <Label htmlFor='status'>{t('serviceDetail.statusDialog.newStatus')}</Label>
            <Select value={newStatus} onValueChange={(value: string | null) => setNewStatus(value as ServiceStatus)}>
              <SelectTrigger id='status'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='pending_review'>{t('serviceDetail.statuses.pendingReview')}</SelectItem>
                <SelectItem value='open'>{t('serviceDetail.statuses.open')}</SelectItem>
                <SelectItem value='pending_payment'>{t('serviceDetail.statuses.pendingPayment')}</SelectItem>
                <SelectItem value='pending_contract'>{t('serviceDetail.statuses.pendingContract')}</SelectItem>
                <SelectItem value='filled'>{t('serviceDetail.statuses.filled')}</SelectItem>
                <SelectItem value='performed'>{t('serviceDetail.statuses.performed')}</SelectItem>
                <SelectItem value='completed'>{t('serviceDetail.statuses.completed')}</SelectItem>
                <SelectItem value='cancelled'>{t('serviceDetail.statuses.cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor='reason'>{t('serviceDetail.statusDialog.reason')} *</Label>
            <Textarea id='reason' placeholder={t('serviceDetail.statusDialog.reasonPlaceholder')} value={reason} onChange={(e) => setReason(e.target.value)} rows={4} />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            {t('serviceDetail.actions.cancel')}
          </Button>
          <Button onClick={handleSubmit}>{t('serviceDetail.statusDialog.confirm')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeletionRequestReviewDialog = ({ mode, open, onOpenChange, serviceId }: { mode: 'approve' | 'reject'; open: boolean; onOpenChange: (open: boolean) => void; serviceId: Id<'duties'> }) => {
  const { t } = useTranslation('operator');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const approveDeletionRequest = useMutation(api.app.operatorServiceMutations.operatorApproveServiceDeletionRequest);
  const rejectDeletionRequest = useMutation(api.app.operatorServiceMutations.operatorRejectServiceDeletionRequest);

  const handleSubmit = async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error(t('serviceDetail.deletionRequest.reviewDialog.toasts.reasonRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === 'approve') {
        await approveDeletionRequest({ serviceId, reason: trimmedReason });
      } else {
        await rejectDeletionRequest({ serviceId, reason: trimmedReason });
      }

      toast.success(mode === 'approve' ? t('serviceDetail.deletionRequest.reviewDialog.toasts.approveSuccess') : t('serviceDetail.deletionRequest.reviewDialog.toasts.rejectSuccess'));
      setReason('');
      onOpenChange(false);
    } catch (error) {
      console.error('Deletion request review error:', error);
      toast.error(mode === 'approve' ? t('serviceDetail.deletionRequest.reviewDialog.toasts.approveError') : t('serviceDetail.deletionRequest.reviewDialog.toasts.rejectError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleKey = mode === 'approve' ? 'serviceDetail.deletionRequest.reviewDialog.approveTitle' : 'serviceDetail.deletionRequest.reviewDialog.rejectTitle';
  const descriptionKey = mode === 'approve' ? 'serviceDetail.deletionRequest.reviewDialog.approveDescription' : 'serviceDetail.deletionRequest.reviewDialog.rejectDescription';
  const confirmKey = mode === 'approve' ? 'serviceDetail.deletionRequest.actions.approve' : 'serviceDetail.deletionRequest.actions.reject';

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) {
          setReason('');
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>{t(descriptionKey)}</DialogDescription>
        </DialogHeader>
        <div className='space-y-2'>
          <Label htmlFor='deletion-review-reason'>{t('serviceDetail.deletionRequest.reviewDialog.reasonLabel')}</Label>
          <Textarea id='deletion-review-reason' value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('serviceDetail.deletionRequest.reviewDialog.reasonPlaceholder')} rows={4} disabled={isSubmitting} />
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('serviceDetail.actions.cancel')}
          </Button>
          <Button variant={mode === 'approve' ? 'destructive' : 'default'} onClick={handleSubmit} disabled={isSubmitting || !reason.trim()}>
            {isSubmitting && <Icons.Loader2 className='size-4 mr-2 animate-spin' />}
            {t(confirmKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RemoveApplicationDialog = ({ application, open, onOpenChange }: { application: OperatorServiceApplication | null; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { t } = useTranslation('operator');
  const removeApplicationMutation = useMutation(api.app.operatorServiceMutations.operatorRemoveApplication);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRemove = async () => {
    if (!application) {
      return;
    }

    setIsSubmitting(true);
    try {
      await removeApplicationMutation({ applicationId: application._id });
      toast.success(t('serviceDetail.applications.toasts.removeSuccess'));
      onOpenChange(false);
    } catch (error) {
      console.error('Application removal error:', error);
      toast.error(error instanceof Error ? error.message : t('serviceDetail.applications.toasts.removeError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <div className='flex items-center gap-3'>
            <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/10'>
              <Icons.AlertTriangle className='size-5 text-destructive' />
            </div>
            <DialogTitle>{t('serviceDetail.applications.removeDialog.title')}</DialogTitle>
          </div>
          <DialogDescription className='pt-2 text-sm leading-relaxed'>
            {t('serviceDetail.applications.removeDialog.description', {
              name: application?.applicantName ?? t('serviceDetail.common.dash'),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className='rounded-lg border border-border/50 bg-muted/30 p-3 text-sm text-muted-foreground'>{t('serviceDetail.applications.removeDialog.note')}</div>

        <DialogFooter className='gap-2 sm:gap-0'>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('serviceDetail.applications.removeDialog.cancel')}
          </Button>
          <Button variant='destructive' onClick={handleRemove} disabled={isSubmitting || !application}>
            {isSubmitting ? t('serviceDetail.applications.removeDialog.submitting') : t('serviceDetail.applications.removeDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Edit Service Dialog
 */
const EditServiceDialog = ({ open, onOpenChange, service }: { open: boolean; onOpenChange: (open: boolean) => void; service: OperatorServiceDuty }) => {
  const { t } = useTranslation('operator');
  const serviceAreas = useQuery(api.app.serviceAreas.listServiceAreas, {}) || [];
  const initialStateCode = getStateCode(service.location.state);
  const [formData, setFormData] = useState({
    title: service.title,
    description: service.description,
    startDate: epochToBerlinDateTimeLocalValue(service.startDate),
    endDate: epochToBerlinDateTimeLocalValue(service.endDate),
    address: service.location.address,
    city: service.location.city,
    stateCode: initialStateCode,
    serviceType: service.serviceType,
    serviceAreaId: service.serviceAreaId || '',
    specialty: service.specialty || '',
    compensationType: service.compensationType || 'fixed',
    minPrice: service.minPrice?.toString() || '',
    maxPrice: service.maxPrice?.toString() || '',
    hasNotfallscheinbeteiligung: service.hasNotfallscheinbeteiligung || false,
    notfallscheinbeteiligungRate: service.notfallscheinbeteiligungRate?.toString() || '',
    requirements: service.requirements || '',
    notes: service.notes || '',
    privateNotes: service.privateNotes || '',
    reason: '',
  });
  const filteredServiceAreas = React.useMemo(() => {
    if (!formData.stateCode) {
      return serviceAreas;
    }

    const kvRegions = KV_REGIONS_BY_STATE[formData.stateCode];
    return serviceAreas.filter((area) => kvRegions.includes(area.kvRegion as (typeof kvRegions)[number]));
  }, [formData.stateCode, serviceAreas]);

  const updateServiceMutation = useMutation(api.app.operatorServiceMutations.operatorUpdateService);

  const handleSubmit = async () => {
    if (!formData.reason.trim()) {
      toast.error(t('serviceDetail.editDialog.toasts.reasonRequired'));
      return;
    }

    try {
      const updates: Record<string, any> = {};

      if (formData.title !== service.title) updates['title'] = formData.title;
      if (formData.description !== service.description) updates['description'] = formData.description;

      const [startDateKey = '', startTime = ''] = formData.startDate.split('T');
      const [endDateKey = '', endTime = ''] = formData.endDate.split('T');
      const newStartDate = berlinWallTimeToEpoch(startDateKey, startTime, service.startDate);
      const newEndDate = berlinWallTimeToEpoch(endDateKey, endTime, service.endDate);
      if (newStartDate === null || newEndDate === null) {
        toast.error('Ungültige Uhrzeit für die deutsche Zeitzone');
        return;
      }
      if (newStartDate !== service.startDate) updates['startDate'] = newStartDate;
      if (newEndDate !== service.endDate) updates['endDate'] = newEndDate;

      const normalizedState = getStateName(formData.stateCode);
      if (formData.address !== service.location.address || formData.city !== service.location.city || (normalizedState || '') !== (service.location.state || '')) {
        updates['location'] = {
          address: formData.address,
          city: formData.city,
          state: normalizedState || undefined,
        };
      }

      if (formData.serviceType !== service.serviceType) updates['serviceType'] = formData.serviceType;
      if (formData.serviceAreaId && formData.serviceAreaId !== (service.serviceAreaId || '')) {
        updates['serviceAreaId'] = formData.serviceAreaId;
      }
      if (formData.specialty !== (service.specialty || '')) updates['specialty'] = formData.specialty || undefined;
      if (formData.compensationType !== (service.compensationType || 'fixed')) updates['compensationType'] = formData.compensationType;

      const minPrice = parseFloat(formData.minPrice);
      const maxPrice = parseFloat(formData.maxPrice);
      if (!isNaN(minPrice) && minPrice !== service.minPrice) updates['minPrice'] = minPrice;
      if (!isNaN(maxPrice) && maxPrice !== service.maxPrice) updates['maxPrice'] = maxPrice;

      if (formData.hasNotfallscheinbeteiligung !== (service.hasNotfallscheinbeteiligung || false)) {
        updates['hasNotfallscheinbeteiligung'] = formData.hasNotfallscheinbeteiligung;
      }

      const notfallscheinRate = parseFloat(formData.notfallscheinbeteiligungRate);
      if (formData.hasNotfallscheinbeteiligung && !isNaN(notfallscheinRate) && notfallscheinRate !== service.notfallscheinbeteiligungRate) {
        updates['notfallscheinbeteiligungRate'] = notfallscheinRate;
      }

      if (formData.requirements !== (service.requirements || '')) updates['requirements'] = formData.requirements || undefined;
      if (formData.notes !== (service.notes || '')) updates['notes'] = formData.notes || undefined;
      if (formData.privateNotes !== (service.privateNotes || '')) updates['privateNotes'] = formData.privateNotes || undefined;

      if (Object.keys(updates).length === 0) {
        toast.error(t('serviceDetail.editDialog.toasts.noChanges'));
        return;
      }

      await updateServiceMutation({
        serviceId: service._id,
        updates,
        reason: formData.reason,
      });

      toast.success(t('serviceDetail.editDialog.toasts.success'));
      onOpenChange(false);
    } catch (error) {
      console.error('Service update error:', error);
      toast.error(t('serviceDetail.editDialog.toasts.error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('serviceDetail.editDialog.title')}</DialogTitle>
          <DialogDescription>{t('serviceDetail.editDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className='space-y-6'>
          {/* Basic Info */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>{t('serviceDetail.editDialog.sections.basic')}</h3>
            <div>
              <Label htmlFor='title'>{t('serviceDetail.editDialog.fields.title')} *</Label>
              <Input id='title' value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
            </div>
            <div>
              <Label htmlFor='description'>{t('serviceDetail.editDialog.fields.description')} *</Label>
              <Textarea id='description' value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} />
            </div>
          </div>

          {/* Date/Time */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>{t('serviceDetail.editDialog.sections.dateTime')}</h3>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='startDate'>{t('serviceDetail.editDialog.fields.startDate')} *</Label>
                <Input id='startDate' type='datetime-local' value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
              </div>
              <div>
                <Label htmlFor='endDate'>{t('serviceDetail.editDialog.fields.endDate')} *</Label>
                <Input id='endDate' type='datetime-local' value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>{t('serviceDetail.editDialog.sections.location')}</h3>
            <div>
              <Label htmlFor='address'>{t('serviceDetail.editDialog.fields.address')} *</Label>
              <Input id='address' value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='city'>{t('serviceDetail.editDialog.fields.city')} *</Label>
                <Input id='city' value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div>
                <Label htmlFor='state'>{t('serviceDetail.editDialog.fields.state')}</Label>
                <Select
                  value={formData.stateCode}
                  onValueChange={(value: string | null) => {
                    if (!value) return;
                    const stateCode = value as FederalStateCode;
                    const allowedKvRegions = KV_REGIONS_BY_STATE[stateCode];
                    const selectedArea = serviceAreas.find((area) => area._id === formData.serviceAreaId);
                    const serviceAreaId = selectedArea && !allowedKvRegions.includes(selectedArea.kvRegion as (typeof allowedKvRegions)[number]) ? '' : formData.serviceAreaId;
                    setFormData({ ...formData, stateCode, serviceAreaId });
                  }}
                >
                  <SelectTrigger id='state'>
                    <SelectValue placeholder={t('serviceDetail.editDialog.fields.statePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {FEDERAL_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Service Type & Specialty */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>{t('serviceDetail.editDialog.sections.serviceType')}</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='serviceType'>{t('serviceDetail.editDialog.fields.serviceType')} *</Label>
                <Select value={formData.serviceType} onValueChange={(value: string | null) => setFormData({ ...formData, serviceType: value as ServiceType })}>
                  <SelectTrigger id='serviceType'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='fahrdienst'>{t('servicesList.serviceTypes.fahrdienst')}</SelectItem>
                    <SelectItem value='sitzdienst'>{t('servicesList.serviceTypes.sitzdienst')}</SelectItem>
                    <SelectItem value='kombidienst'>{t('servicesList.serviceTypes.kombidienst')}</SelectItem>
                    <SelectItem value='hintergrunddienst'>{t('servicesList.serviceTypes.hintergrunddienst')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor='serviceAreaId'>{t('serviceDetail.editDialog.fields.serviceArea')}</Label>
                <Select value={formData.serviceAreaId} onValueChange={(value: string | null) => setFormData({ ...formData, serviceAreaId: value || '' })}>
                  <SelectTrigger id='serviceAreaId'>
                    <SelectValue placeholder={t('serviceDetail.editDialog.fields.serviceAreaPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredServiceAreas.map((area) => (
                      <SelectItem key={area._id} value={area._id}>
                        {area.name} ({area.kvRegion})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor='specialty'>{t('serviceDetail.editDialog.fields.specialty')}</Label>
                <Input id='specialty' value={formData.specialty} onChange={(e) => setFormData({ ...formData, specialty: e.target.value })} placeholder={t('serviceDetail.editDialog.fields.specialtyPlaceholder')} />
              </div>
            </div>
          </div>

          {/* Compensation */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>{t('serviceDetail.editDialog.sections.compensation')}</h3>
            <div>
              <Label htmlFor='compensationType'>{t('serviceDetail.editDialog.fields.compensationType')} *</Label>
              <Select value={formData.compensationType} onValueChange={(value: string | null) => setFormData({ ...formData, compensationType: value as CompensationType })}>
                <SelectTrigger id='compensationType'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='fixed'>{t('serviceDetail.editDialog.fields.compensationFixed')}</SelectItem>
                  <SelectItem value='range'>{t('serviceDetail.editDialog.fields.compensationRange')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='minPrice'>{formData.compensationType === 'fixed' ? t('serviceDetail.editDialog.fields.hourlyRate') : t('serviceDetail.editDialog.fields.minPrice')} *</Label>
                <Input id='minPrice' type='number' min='0' step='0.01' value={formData.minPrice} onChange={(e) => setFormData({ ...formData, minPrice: e.target.value })} />
              </div>
              {formData.compensationType === 'range' && (
                <div>
                  <Label htmlFor='maxPrice'>{t('serviceDetail.editDialog.fields.maxPrice')} *</Label>
                  <Input id='maxPrice' type='number' min='0' step='0.01' value={formData.maxPrice} onChange={(e) => setFormData({ ...formData, maxPrice: e.target.value })} />
                </div>
              )}
            </div>
          </div>

          {/* Notfallscheinbeteiligung */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>{t('serviceDetail.editDialog.sections.emergencyParticipation')}</h3>
            <div className='flex items-center gap-2'>
              <Checkbox
                id='hasNotfallscheinbeteiligung'
                checked={formData.hasNotfallscheinbeteiligung}
                onCheckedChange={(checked: boolean) =>
                  setFormData({
                    ...formData,
                    hasNotfallscheinbeteiligung: checked === true,
                  })
                }
              />
              <Label htmlFor='hasNotfallscheinbeteiligung' className='cursor-pointer'>
                {t('serviceDetail.editDialog.fields.enableEmergencyParticipation')}
              </Label>
            </div>
            {formData.hasNotfallscheinbeteiligung && (
              <div>
                <Label htmlFor='notfallscheinbeteiligungRate'>{t('serviceDetail.editDialog.fields.amountPerPatient')} *</Label>
                <Input
                  id='notfallscheinbeteiligungRate'
                  type='number'
                  min='5'
                  max={maxNotfallscheinbeteiligungRate(formData.serviceType)}
                  step='0.01'
                  value={formData.notfallscheinbeteiligungRate}
                  onChange={(e) => setFormData({ ...formData, notfallscheinbeteiligungRate: e.target.value })}
                  placeholder={t('serviceDetail.editDialog.fields.amountPerPatientPlaceholder')}
                />
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>{t('serviceDetail.editDialog.sections.additionalInfo')}</h3>
            <div>
              <Label htmlFor='requirements'>{t('serviceDetail.editDialog.fields.requirements')}</Label>
              <Textarea id='requirements' value={formData.requirements} onChange={(e) => setFormData({ ...formData, requirements: e.target.value })} rows={3} placeholder={t('serviceDetail.editDialog.fields.requirementsPlaceholder')} />
            </div>
            <div>
              <Label htmlFor='notes'>{t('serviceDetail.editDialog.fields.publicNotes')}</Label>
              <Textarea id='notes' value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder={t('serviceDetail.editDialog.fields.publicNotesPlaceholder')} />
            </div>
            <div>
              <Label htmlFor='privateNotes'>{t('serviceDetail.editDialog.fields.privateNotes')}</Label>
              <Textarea id='privateNotes' value={formData.privateNotes} onChange={(e) => setFormData({ ...formData, privateNotes: e.target.value })} rows={3} placeholder={t('serviceDetail.editDialog.fields.privateNotesPlaceholder')} />
            </div>
          </div>

          {/* Reason */}
          <div className='space-y-4 border-t pt-4'>
            <div>
              <Label htmlFor='reason'>{t('serviceDetail.editDialog.fields.reason')} *</Label>
              <Textarea id='reason' placeholder={t('serviceDetail.editDialog.fields.reasonPlaceholder')} value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} rows={3} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            {t('serviceDetail.actions.cancel')}
          </Button>
          <Button onClick={handleSubmit}>{t('serviceDetail.editDialog.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * All 17 KV Regions in Germany
 */
const ALL_KV_REGIONS = [
  'KV Baden-Württemberg',
  'KV Bayern',
  'KV Berlin',
  'KV Brandenburg',
  'KV Bremen',
  'KV Hamburg',
  'KV Hessen',
  'KV Mecklenburg-Vorpommern',
  'KV Niedersachsen',
  'KV Nordrhein',
  'KV Rheinland-Pfalz',
  'KV Saarland',
  'KV Sachsen',
  'KV Sachsen-Anhalt',
  'KV Schleswig-Holstein',
  'KV Thüringen',
  'KV Westfalen-Lippe',
].sort();

const pickFirstText = (...values: Array<string | undefined>): string | undefined => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }

  return undefined;
};

type OperatorTranslator = TFunction<'operator', undefined>;

type NotificationDutyDetails = Pick<
  OperatorServiceDuty,
  'serviceType' | 'startDate' | 'endDate' | 'compensationType' | 'minPrice' | 'maxPrice' | 'hasNotfallscheinbeteiligung' | 'notfallscheinbeteiligungRate' | 'manualServiceArea' | 'manualBereitschaftspraxis' | 'location'
>;

type NotificationServiceDetails = {
  duty: NotificationDutyDetails;
  serviceAreaName?: string;
  serviceAreaKvRegion?: string;
  manualServiceArea?: string;
  bereitschaftspraxisName?: string;
};

const getFederalStateFromKvRegion = (kvRegion?: string): string | undefined => {
  if (!kvRegion) return undefined;

  const stateCode = FEDERAL_STATES.find((state) => KV_REGIONS_BY_STATE[state.code].includes(kvRegion as (typeof KV_REGIONS_BY_STATE)[keyof typeof KV_REGIONS_BY_STATE][number]))?.code;
  return stateCode ? getStateName(stateCode) : undefined;
};

const formatEmailSubjectDate = (timestamp: number, language: string): string =>
  new Date(timestamp).toLocaleDateString(language.startsWith('en') ? 'en-US' : 'de-DE', {
    timeZone: 'Europe/Berlin',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });

const getNotificationServiceArea = (serviceDetails: NotificationServiceDetails, notProvided: string): string =>
  pickFirstText(serviceDetails.serviceAreaName, serviceDetails.manualServiceArea, serviceDetails.duty.manualServiceArea, serviceDetails.duty.location?.city) || notProvided;

const getNotificationLocation = (serviceDetails: NotificationServiceDetails, notProvided: string): string =>
  pickFirstText(serviceDetails.bereitschaftspraxisName, serviceDetails.duty.manualBereitschaftspraxis, serviceDetails.duty.location?.city) || notProvided;

const getNotificationFederalState = (serviceDetails: NotificationServiceDetails, notProvided: string): string => pickFirstText(serviceDetails.duty.location?.state, getFederalStateFromKvRegion(serviceDetails.serviceAreaKvRegion)) || notProvided;

const generateServiceNotificationSubject = (serviceDetails: NotificationServiceDetails, t: OperatorTranslator, language: string): string => {
  const service = serviceDetails.duty;
  const serviceType = getServiceTypeLabel(service.serviceType || 'fahrdienst', (key) => t(key));
  const serviceArea = getNotificationServiceArea(serviceDetails, t('serviceDetail.emailDialog.notProvided'));
  const federalState = getNotificationFederalState(serviceDetails, '');
  const scopedArea = federalState ? `${serviceArea} (${federalState})` : serviceArea;

  return `${t('serviceDetail.emailDialog.subjectPrefix')}: ${serviceType} - ${scopedArea} - ${formatEmailSubjectDate(service.startDate, language)}`;
};

/**
 * Generate email template based on service details
 */
const generateEmailTemplate = (serviceDetails: NotificationServiceDetails, t: OperatorTranslator, language: string): string => {
  const service = serviceDetails.duty;
  const serviceType = getServiceTypeLabel(service.serviceType || 'fahrdienst', (key) => t(key));
  const duration = ((service.endDate - service.startDate) / (1000 * 60 * 60)).toFixed(1);
  const compensation = service.compensationType === 'range' ? `${service.minPrice}€ - ${service.maxPrice}€` : `${service.minPrice || service.maxPrice}€`;
  const notfallschein = service.hasNotfallscheinbeteiligung && typeof service.notfallscheinbeteiligungRate === 'number' ? `${service.notfallscheinbeteiligungRate}€ pro Patient` : '0€';
  const notProvided = t('serviceDetail.emailDialog.notProvided');
  const location = getNotificationLocation(serviceDetails, notProvided);
  const serviceArea = getNotificationServiceArea(serviceDetails, notProvided);
  const federalState = getNotificationFederalState(serviceDetails, notProvided);

  const startDate = formatDate(service.startDate, language);
  const endDate = formatDate(service.endDate, language);

  return t('serviceDetail.emailDialog.defaultMessage', {
    location,
    serviceArea,
    federalState,
    serviceType,
    duration,
    startDate,
    endDate,
    compensation,
    notfallschein,
  });
};

/**
 * Send Email Dialog
 */
export const SendEmailDialog = ({ open, onOpenChange, serviceId, serviceTitle }: { open: boolean; onOpenChange: (open: boolean) => void; serviceId: Id<'duties'>; serviceTitle: string }) => {
  const { t, i18n } = useTranslation('operator');
  const [filter, setFilter] = useState<'all' | 'kv_region' | 'email'>('all');
  const [kvRegion, setKvRegion] = useState('');
  const [customEmails, setCustomEmails] = useState('');
  const initialSubject = `${t('serviceDetail.emailDialog.subjectPrefix')}: ${serviceTitle}`;
  const [subject, setSubject] = useState(initialSubject);
  const [isSending, setIsSending] = useState(false);

  const sendEmailAction = useAction(api.app.operatorActions.sendServiceNotification);

  // Query service details to generate email template
  const serviceDetails = useQuery(api.app.operatorServiceQueries.getServiceDetails, {
    serviceId: serviceId as Id<'duties'>,
  });

  // Generate message template when service details are available
  const [message, setMessage] = useState('');

  React.useEffect(() => {
    if (serviceDetails?.duty && !message) {
      setMessage(generateEmailTemplate(serviceDetails, t, i18n.language));
    }
  }, [serviceDetails, message, t, i18n.language]);

  React.useEffect(() => {
    if (!serviceDetails) return;
    const nextSubject = generateServiceNotificationSubject(serviceDetails, t, i18n.language);
    setSubject((currentSubject) => (currentSubject.trim() && currentSubject !== initialSubject ? currentSubject : nextSubject));
  }, [serviceDetails, initialSubject, t, i18n.language]);

  const handleSubmit = async () => {
    if (isSending) {
      return;
    }

    if (filter === 'kv_region' && !kvRegion) {
      toast.error(t('serviceDetail.emailDialog.toasts.kvRegionRequired'));
      return;
    }

    if (filter === 'email' && !customEmails.trim()) {
      toast.error(t('serviceDetail.emailDialog.toasts.emailsRequired'));
      return;
    }

    if (!subject.trim()) {
      toast.error(t('serviceDetail.emailDialog.toasts.subjectRequired'));
      return;
    }

    if (!message.trim()) {
      toast.error(t('serviceDetail.emailDialog.toasts.messageRequired'));
      return;
    }

    let sendingToastId: string | number | undefined;

    try {
      const actionArgs: {
        serviceId: Id<'duties'>;
        filter: 'all' | 'kv_region' | 'email';
        kvRegion?: string;
        customEmails?: string;
        subject: string;
        message: string;
        language: 'de' | 'en';
      } = {
        serviceId,
        filter,
        subject,
        message,
        language: i18n.language.startsWith('en') ? 'en' : 'de',
      };

      if (filter === 'kv_region' && kvRegion) {
        actionArgs.kvRegion = kvRegion;
      }

      if (filter === 'email' && customEmails) {
        actionArgs.customEmails = customEmails;
      }

      setIsSending(true);
      sendingToastId = toast.info(t('serviceDetail.emailDialog.toasts.sending'), {
        description: t('serviceDetail.emailDialog.toasts.sendingDescription'),
        duration: Infinity,
      });

      const result = await sendEmailAction(actionArgs);

      const outcome = getServiceNotificationOutcome(result);

      switch (outcome) {
        case 'noRecipients':
          toast.info(t('serviceDetail.emailDialog.toasts.noRecipients'));
          break;
        case 'partialSuccess':
          toast.warning(t('serviceDetail.emailDialog.toasts.partialSuccess', { count: result.count, failCount: result.failCount }));
          break;
        case 'success':
          toast.success(t('serviceDetail.emailDialog.toasts.successWithCount', { count: result.count }));
          break;
        case 'error':
          toast.error(t('serviceDetail.emailDialog.toasts.error'));
          break;
      }

      if (outcome !== 'error') {
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Send email error:', error);
      toast.error(t('serviceDetail.emailDialog.toasts.error'));
    } finally {
      if (sendingToastId !== undefined) {
        toast.dismiss(sendingToastId);
      }
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSending) onOpenChange(nextOpen);
      }}
    >
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('serviceDetail.emailDialog.title')}</DialogTitle>
          <DialogDescription>{t('serviceDetail.emailDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className='space-y-6'>
          {/* Filter Selection */}
          <div className='space-y-3'>
            <Label>{t('serviceDetail.emailDialog.recipients')}</Label>
            <RadioGroup value={filter} onValueChange={(value: string | null) => setFilter(value as 'all' | 'kv_region' | 'email')} disabled={isSending}>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='all' id='all' />
                <Label htmlFor='all' className='cursor-pointer'>
                  {t('serviceDetail.emailDialog.recipientModes.all')}
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='kv_region' id='kv_region' />
                <Label htmlFor='kv_region' className='cursor-pointer'>
                  {t('serviceDetail.emailDialog.recipientModes.kvRegion')}
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='email' id='email' />
                <Label htmlFor='email' className='cursor-pointer'>
                  {t('serviceDetail.emailDialog.recipientModes.customEmails')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* KV Region Selection */}
          {filter === 'kv_region' && (
            <div className='space-y-2 pl-6'>
              <Label htmlFor='kvRegion'>{t('serviceDetail.emailDialog.kvRegion')} *</Label>
              <Select
                value={kvRegion}
                onValueChange={(v) => {
                  if (v !== null) setKvRegion(v);
                }}
              >
                <SelectTrigger id='kvRegion' disabled={isSending}>
                  <SelectValue placeholder={t('serviceDetail.emailDialog.kvRegionPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {ALL_KV_REGIONS.map((region) => (
                    <SelectItem key={region} value={region}>
                      {region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Custom Email Input */}
          {filter === 'email' && (
            <div className='space-y-2 pl-6'>
              <Label htmlFor='customEmails'>{t('serviceDetail.emailDialog.customEmails')} *</Label>
              <Textarea id='customEmails' value={customEmails} onChange={(e) => setCustomEmails(e.target.value)} rows={3} placeholder={t('serviceDetail.emailDialog.customEmailsPlaceholder')} disabled={isSending} />
              <p className='text-xs text-muted-foreground'>{t('serviceDetail.emailDialog.customEmailsHint')}</p>
            </div>
          )}

          {/* Email Content */}
          <div className='space-y-4'>
            <div>
              <Label htmlFor='subject'>{t('serviceDetail.emailDialog.subject')} *</Label>
              <Input id='subject' value={subject} onChange={(e) => setSubject(e.target.value)} disabled={isSending} />
            </div>
            <div>
              <Label htmlFor='message'>{t('serviceDetail.emailDialog.message')} *</Label>
              <Textarea id='message' value={message} onChange={(e) => setMessage(e.target.value)} rows={12} disabled={isSending} />
              <p className='text-xs text-muted-foreground mt-1'>{t('serviceDetail.emailDialog.messageHint')}</p>
            </div>
          </div>
          {isSending && (
            <div className='flex items-start gap-3 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm'>
              <Icons.Loader2 className='mt-0.5 size-4 shrink-0 animate-spin text-primary' />
              <div className='space-y-1'>
                <p className='font-medium text-foreground'>{t('serviceDetail.emailDialog.sending.title')}</p>
                <p className='text-muted-foreground'>{t('serviceDetail.emailDialog.sending.description')}</p>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={isSending}>
            {t('serviceDetail.actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSending}>
            {isSending ? <Icons.Loader2 className='size-4 mr-2 animate-spin' /> : <Icons.Send className='size-4 mr-2' />}
            {isSending ? t('serviceDetail.emailDialog.sending.button') : t('serviceDetail.actions.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Service Details Page Component
 */
/**
 * Regenerate Contract Confirmation Dialog
 * Shows current contract acceptance status and warns about re-acceptance requirement
 */
const RegenerateContractDialog = ({
  open,
  onOpenChange,
  contract,
  onConfirm,
  isGenerating,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: {
    version: number;
    providerAcceptedAt?: number;
    substituteAcceptedAt?: number;
  };
  onConfirm: () => void;
  isGenerating: boolean;
}) => {
  const { t, i18n } = useTranslation('operator');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('serviceDetail.regenerateDialog.title')}</DialogTitle>
          <DialogDescription>{t('serviceDetail.regenerateDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='rounded-lg border p-4 space-y-3'>
            <p className='text-sm font-medium'>{t('serviceDetail.regenerateDialog.currentVersion', { version: contract.version })}</p>
            <div className='space-y-2'>
              <div className='flex items-center gap-2 text-sm'>
                <span className='text-muted-foreground w-20'>{t('serviceDetail.regenerateDialog.provider')}</span>
                {contract.providerAcceptedAt ? (
                  <span className='text-primary flex items-center gap-1'>
                    <span>{'\u2713'}</span>
                    <span>{t('serviceDetail.regenerateDialog.acceptedAt', { date: formatDate(contract.providerAcceptedAt, i18n.language) })}</span>
                  </span>
                ) : (
                  <span className='text-muted-foreground flex items-center gap-1'>
                    <span>{'\u25CB'}</span>
                    <span>{t('serviceDetail.regenerateDialog.pending')}</span>
                  </span>
                )}
              </div>
              <div className='flex items-center gap-2 text-sm'>
                <span className='text-muted-foreground w-20'>{t('serviceDetail.regenerateDialog.substitute')}</span>
                {contract.substituteAcceptedAt ? (
                  <span className='text-primary flex items-center gap-1'>
                    <span>{'\u2713'}</span>
                    <span>{t('serviceDetail.regenerateDialog.acceptedAt', { date: formatDate(contract.substituteAcceptedAt, i18n.language) })}</span>
                  </span>
                ) : (
                  <span className='text-muted-foreground flex items-center gap-1'>
                    <span>{'\u25CB'}</span>
                    <span>{t('serviceDetail.regenerateDialog.pending')}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className='flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20'>
            <Icons.AlertTriangle className='size-5 text-warning mt-0.5 shrink-0' />
            <p className='text-sm text-warning-foreground'>{t('serviceDetail.regenerateDialog.warning')}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={isGenerating}>
            {t('serviceDetail.actions.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Icons.Loader2 className='size-4 mr-2 animate-spin' />
                {t('serviceDetail.regenerateDialog.generating')}
              </>
            ) : (
              t('serviceDetail.regenerateDialog.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OperatorServiceDetailsPage = () => {
  const { t, i18n } = useTranslation('operator');
  const { serviceId } = Route.useParams();
  const navigate = useNavigate();
  const convex = useConvex();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deletionReviewDialog, setDeletionReviewDialog] = useState<'approve' | 'reject' | null>(null);
  const [applicationToEdit, setApplicationToEdit] = useState<OperatorServiceApplication | null>(null);
  const [applicationToRemove, setApplicationToRemove] = useState<OperatorServiceApplication | null>(null);
  const [isGeneratingHonorarvertrag, setIsGeneratingHonorarvertrag] = useState(false);
  const [isConvertingToInvoice, setIsConvertingToInvoice] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [invoiceRepairTarget, setInvoiceRepairTarget] = useState<PlatformInvoiceRepairTarget | null>(null);
  const [diensttauschDialog, setDiensttauschDialog] = useState<{ draft: OperatorDiensttauschFormularDraft; regenerate: boolean } | null>(null);

  console.log('OperatorServiceDetailsPage rendered, serviceId:', serviceId);

  // Query service details
  const serviceDetails = useQuery(api.app.operatorServiceQueries.getServiceDetails, {
    serviceId: serviceId as Id<'duties'>,
  }) as OperatorServiceDetails | undefined;
  const diensttauschDraft = useQuery(api.app.diensttauschFormulareQueries.getOperatorFormularDialogDraft, serviceDetails === undefined ? 'skip' : { dutyId: serviceId as Id<'duties'> });

  // Action to generate honorarvertrag
  const generateHonorarvertragAction = useAction(api.app.contracts.operatorGenerateContract);
  const repairPlatformFeeInvoiceAction = useAction(api.app.payments.invoiceActions.repairPlatformFeeInvoice);
  const convertToInvoiceAction = useAction(api.app.payments.platformFeeActions.operatorConvertPendingBookingToInvoice);

  const handleConvertToInvoice = async (dutyId: Id<'duties'>): Promise<void> => {
    setIsConvertingToInvoice(true);
    try {
      const result = await convertToInvoiceAction({ dutyId });
      if (result.success) toast.success(result.message);
      else toast.error(result.message);
    } catch (error) {
      console.error('Invoice conversion error:', error);
      toast.error(t('serviceDetail.payments.convertToInvoiceError'));
    } finally {
      setIsConvertingToInvoice(false);
    }
  };

  const handleRepairPlatformFeeInvoice = async (paymentId: Id<'payments'>, refreshIssuerDetails: boolean): Promise<void> => {
    const result = await repairPlatformFeeInvoiceAction({ paymentId, refreshIssuerDetails });
    toast.success(t(result.status === 'generated' ? 'serviceDetail.toasts.invoiceRepairGenerated' : 'serviceDetail.toasts.invoiceRepairAlreadyAvailable', { invoiceNumber: result.invoiceNumber }));
  };

  const repairBookingFinalization = useMutation(api.app.operatorServiceMutations.operatorRepairProviderBookingFinalization);
  const [repairingBookingPaymentId, setRepairingBookingPaymentId] = useState<Id<'payments'> | null>(null);

  const handleRepairBookingFinalization = async (paymentId: Id<'payments'>): Promise<void> => {
    setRepairingBookingPaymentId(paymentId);
    try {
      await repairBookingFinalization({ paymentId });
      toast.success(t('serviceDetail.toasts.bookingRepaired'));
    } catch (error) {
      const reason = error instanceof ConvexError && typeof error.data === 'string' ? BOOKING_REPAIR_ERROR_KEYS[error.data] : undefined;
      toast.error(t(reason ? `serviceDetail.toasts.bookingRepairErrors.${reason}` : 'serviceDetail.toasts.bookingRepairFailed'));
    } finally {
      setRepairingBookingPaymentId(null);
    }
  };

  console.log('serviceDetails:', serviceDetails);

  const isLoading = serviceDetails === undefined;

  if (isLoading) {
    return (
      <div className='container mx-auto py-8 space-y-6'>
        <Skeleton className='h-10 w-48' />
        <DetailsSkeleton />
      </div>
    );
  }

  if (!serviceDetails) {
    return (
      <div className='container mx-auto py-8'>
        <Card>
          <CardContent className='py-16 text-center'>
            <Icons.AlertCircle className='size-12 text-destructive mx-auto mb-3' />
            <p className='text-muted-foreground'>{t('serviceDetail.notFound')}</p>
            <Button variant='outline' className='mt-4' onClick={() => navigate({ to: '/operators/services' })}>
              {t('serviceDetail.backToList')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { duty, owner, assignee, applications, documents, payments, audits, serviceAreaName, manualServiceArea } = serviceDetails;
  const paymentsById = new Map(payments.map((payment) => [payment._id, payment]));
  const invoicesByPaymentId = new Map(serviceDetails.platformInvoices.map((invoice) => [invoice.paymentId, invoice]));
  const deletionRequestStatus = duty.deletionRequestStatus ?? (duty.deletionRequested ? 'pending' : undefined);
  const isArchived = duty.deletedAt !== undefined;

  const formatCompensation = (): string => {
    if (duty.compensationType === 'range') {
      return `${duty.minPrice}€ - ${duty.maxPrice}€/h`;
    }
    return `${duty.minPrice || duty.maxPrice}€/h`;
  };

  const calculateDuration = (): string => {
    const hours = (duty.endDate - duty.startDate) / (1000 * 60 * 60);
    return `${hours.toFixed(1)}h`;
  };

  /**
   * Perform the actual Honorarvertrag generation
   */
  const handleGenerateHonorarvertrag = async () => {
    setIsGeneratingHonorarvertrag(true);
    try {
      const result = await generateHonorarvertragAction({ dutyId: duty._id });

      if (result.success) {
        toast.success(result.message || t('serviceDetail.toasts.contractGenerated'));
        setRegenerateDialogOpen(false);
      } else {
        toast.error(result.message || t('serviceDetail.toasts.contractGenerateError'));
      }
    } catch (error) {
      console.error('Honorarvertrag generation error:', error);
      toast.error(t('serviceDetail.toasts.contractGenerateError'));
    } finally {
      setIsGeneratingHonorarvertrag(false);
    }
  };

  /**
   * Handle click on generate Honorarvertrag button
   * Shows confirmation dialog if the latest contract has at least one acceptance
   * Otherwise, generates directly
   */
  const handleGenerateHonorarvertragClick = () => {
    // Check if there are existing contracts
    if (serviceDetails && (serviceDetails.honorarvertragContracts?.length ?? 0) > 0) {
      // Get the latest contract (first in the array, assuming sorted by version desc or creation time desc)
      const latestContract = serviceDetails.honorarvertragContracts[0];

      // Check if either party has accepted the current contract
      const hasAnyAcceptance = latestContract.providerAcceptedAt || latestContract.substituteAcceptedAt;

      if (hasAnyAcceptance) {
        // Show confirmation dialog
        setRegenerateDialogOpen(true);
        return;
      }
    }

    // No existing contracts with acceptances - generate directly
    handleGenerateHonorarvertrag();
  };

  /**
   * Get badge variant for honorarvertrag contract status
   */
  const getHonorarvertragStatusBadge = (status: string): { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' } => {
    switch (status) {
      case 'accepted':
        return { label: t('serviceDetail.contracts.statuses.accepted'), variant: 'default' };
      case 'pending_provider_acceptance':
      case 'pending_substitute_upload':
      case 'changes_requested':
        return { label: t('serviceDetail.contracts.statuses.pendingAcceptance'), variant: 'secondary' };
      case 'superseded':
        return { label: t('serviceDetail.contracts.statuses.superseded'), variant: 'outline' };
      case 'rejected':
        return { label: t('serviceDetail.contracts.statuses.rejected'), variant: 'destructive' };
      case 'expired':
        return { label: t('serviceDetail.contracts.statuses.expired'), variant: 'outline' };
      case 'pending_cancellation':
        return { label: t('serviceDetail.contracts.statuses.pendingCancellation'), variant: 'secondary' };
      default:
        return { label: status, variant: 'outline' };
    }
  };

  return (
    <div className='container mx-auto py-8 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='space-y-1'>
          <div className='flex items-center gap-2'>
            <Link to='/operators/services'>
              <Button variant='ghost' size='sm'>
                <Icons.ChevronLeft className='size-4 mr-1' />
                {t('serviceDetail.back')}
              </Button>
            </Link>
          </div>
          <h1 className='text-3xl font-bold tracking-tight'>{duty.title}</h1>
          <div className='flex items-center gap-2'>
            <ServiceStatusBadge status={duty.status} />
            <Badge variant='outline'>{getServiceTypeLabel(duty.serviceType, t)}</Badge>
            {duty.hasNotfallscheinbeteiligung && <Badge variant='secondary'>{t('serviceDetail.labels.emergencyParticipation')}</Badge>}
          </div>
        </div>
        <div className='flex gap-2'>
          <Button variant='outline' onClick={() => setEditDialogOpen(true)} disabled={isArchived}>
            <Icons.Pencil className='size-4 mr-2' />
            {t('serviceDetail.actions.edit')}
          </Button>
          <Button variant='outline' onClick={() => setStatusDialogOpen(true)} disabled={isArchived}>
            <Icons.RefreshCw className='size-4 mr-2' />
            {t('serviceDetail.actions.changeStatus')}
          </Button>
          <Button variant='outline' onClick={() => setAssignDialogOpen(true)} disabled={isArchived || !duty || ['completed', 'cancelled'].includes(duty.status)}>
            <Icons.UserPlus className='size-4 mr-2' />
            {t('serviceDetail.actions.assignSubstitute')}
          </Button>
          <Button variant='outline' onClick={() => setEmailDialogOpen(true)} disabled={isArchived}>
            <Icons.Mail className='size-4 mr-2' />
            {t('serviceDetail.actions.sendEmail')}
          </Button>
        </div>
      </div>

      {deletionRequestStatus === 'pending' && (
        <Card className='border-warning/30 bg-warning/10'>
          <CardContent className='flex items-start gap-3 p-4'>
            <Icons.AlertTriangle className='size-5 shrink-0 text-warning mt-0.5' />
            <div className='flex-1 space-y-1'>
              <p className='text-sm font-medium text-warning'>{t('serviceDetail.deletionRequest.title')}</p>
              <p className='text-sm text-warning/90'>
                {t('serviceDetail.deletionRequest.description', {
                  date: duty.deletionRequestedAt ? formatDate(duty.deletionRequestedAt, i18n.language) : t('serviceDetail.deletionRequest.unknownDate'),
                })}
              </p>
              {duty.deletionReason && (
                <p className='text-sm text-warning/90'>
                  <span className='font-medium'>{t('serviceDetail.deletionRequest.requestReasonLabel')}</span> {duty.deletionReason}
                </p>
              )}
              <div className='flex flex-wrap gap-2 pt-2'>
                <Button size='sm' variant='destructive' onClick={() => setDeletionReviewDialog('approve')}>
                  {t('serviceDetail.deletionRequest.actions.approve')}
                </Button>
                <Button size='sm' variant='outline' onClick={() => setDeletionReviewDialog('reject')}>
                  {t('serviceDetail.deletionRequest.actions.reject')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {deletionRequestStatus === 'rejected' && (
        <Card className='border-destructive/20 bg-destructive/5'>
          <CardContent className='flex items-start gap-3 p-4'>
            <Icons.AlertTriangle className='size-5 shrink-0 text-destructive mt-0.5' />
            <div className='space-y-1'>
              <p className='text-sm font-medium text-destructive'>{t('serviceDetail.deletionRequest.rejectedTitle')}</p>
              <p className='text-sm text-destructive/90'>
                {t('serviceDetail.deletionRequest.rejectedDescription', {
                  date: duty.deletionRequestReviewedAt ? formatDate(duty.deletionRequestReviewedAt, i18n.language) : t('serviceDetail.deletionRequest.unknownDate'),
                })}
              </p>
              {duty.deletionReason && (
                <p className='text-sm text-destructive/90'>
                  <span className='font-medium'>{t('serviceDetail.deletionRequest.requestReasonLabel')}</span> {duty.deletionReason}
                </p>
              )}
              {duty.deletionRequestReviewReason && (
                <p className='text-sm text-destructive/90'>
                  <span className='font-medium'>{t('serviceDetail.deletionRequest.operatorReasonLabel')}</span> {duty.deletionRequestReviewReason}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {deletionRequestStatus === 'approved' && (
        <Card className='border-primary/20 bg-primary/5'>
          <CardContent className='flex items-start gap-3 p-4'>
            <Icons.CheckCircle className='size-5 shrink-0 text-primary mt-0.5' />
            <div className='space-y-1'>
              <p className='text-sm font-medium text-primary'>{t('serviceDetail.deletionRequest.approvedTitle')}</p>
              <p className='text-sm text-primary/90'>
                {t('serviceDetail.deletionRequest.approvedDescription', {
                  date: duty.deletionRequestReviewedAt ? formatDate(duty.deletionRequestReviewedAt, i18n.language) : t('serviceDetail.deletionRequest.unknownDate'),
                })}
              </p>
              {duty.deletionReason && (
                <p className='text-sm text-primary/90'>
                  <span className='font-medium'>{t('serviceDetail.deletionRequest.requestReasonLabel')}</span> {duty.deletionReason}
                </p>
              )}
              {duty.deletionRequestReviewReason && (
                <p className='text-sm text-primary/90'>
                  <span className='font-medium'>{t('serviceDetail.deletionRequest.operatorReasonLabel')}</span> {duty.deletionRequestReviewReason}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <Card>
          <CardHeader className='pb-3'>
            <CardDescription>{t('serviceDetail.quickStats.provider')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col'>
              <span className='font-medium'>{owner.firstName && owner.lastName ? `${owner.firstName} ${owner.lastName}` : owner.email}</span>
              <span className='text-sm text-muted-foreground'>{owner.email}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='pb-3'>
            <CardDescription>{t('serviceDetail.quickStats.substitute')}</CardDescription>
          </CardHeader>
          <CardContent>
            {assignee ? (
              <div className='flex flex-col'>
                <Link to='/operators/users/$userId' params={{ userId: assignee._id }} className='font-medium text-primary hover:underline'>
                  {assignee.firstName && assignee.lastName ? `${assignee.firstName} ${assignee.lastName}` : assignee.email}
                </Link>
                <span className='text-sm text-muted-foreground'>{assignee.email}</span>
              </div>
            ) : (
              <span className='text-sm text-muted-foreground'>{t('serviceDetail.quickStats.unassigned')}</span>
            )}
          </CardContent>{' '}
        </Card>

        <Card>
          <CardHeader className='pb-3'>
            <CardDescription>{t('serviceDetail.quickStats.compensation')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col'>
              <span className='font-medium'>{formatCompensation()}</span>
              <span className='text-sm text-muted-foreground'>{t('serviceDetail.quickStats.duration', { value: calculateDuration() })}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='pb-3'>
            <CardDescription>{t('serviceDetail.quickStats.applications')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col'>
              <span className='font-medium'>{applications.length}</span>
              <span className='text-sm text-muted-foreground'>{t('serviceDetail.quickStats.pendingCount', { count: applications.filter((a) => a.status === 'pending').length })}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Content */}
      <Tabs defaultValue='details' className='space-y-4'>
        <TabsList>
          <TabsTrigger value='details'>{t('serviceDetail.tabs.details')}</TabsTrigger>
          <TabsTrigger value='applications'>
            {t('serviceDetail.tabs.applications')}
            {applications.length > 0 && (
              <Badge variant='secondary' className='ml-2'>
                {applications.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='documents'>
            {t('serviceDetail.tabs.documents')}
            {documents.length > 0 && (
              <Badge variant='secondary' className='ml-2'>
                {documents.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='activity-report'>
            {t('serviceDetail.tabs.activityReport')}
            {duty.activityReport && (
              <Badge variant='secondary' className='ml-2'>
                {duty.activityReportConfirmedAt ? '✓' : '!'}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='payments'>
            {t('serviceDetail.tabs.payments')}
            {payments.length > 0 && (
              <Badge variant='secondary' className='ml-2'>
                {payments.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='audit'>
            {t('serviceDetail.tabs.audit')}
            {audits.length > 0 && (
              <Badge variant='secondary' className='ml-2'>
                {audits.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value='details'>
          <Card>
            <CardHeader>
              <CardTitle>{t('serviceDetail.details.title')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <Label className='text-muted-foreground'>{t('serviceDetail.details.startDate')}</Label>
                  <p className='mt-1'>{formatDate(duty.startDate, i18n.language)}</p>
                </div>
                <div>
                  <Label className='text-muted-foreground'>{t('serviceDetail.details.endDate')}</Label>
                  <p className='mt-1'>{formatDate(duty.endDate, i18n.language)}</p>
                </div>
                <div>
                  <Label className='text-muted-foreground'>{t('serviceDetail.details.location')}</Label>
                  <p className='mt-1'>{duty.location.address}</p>
                  <p className='text-sm text-muted-foreground'>
                    {duty.location.city}
                    {duty.location.state && `, ${duty.location.state}`}
                  </p>
                </div>
                <div>
                  <Label className='text-muted-foreground'>{t('serviceDetail.details.serviceType')}</Label>
                  <p className='mt-1'>{getServiceTypeLabel(duty.serviceType, t)}</p>
                </div>
                {/* DA-474: Display service area name */}
                {(serviceAreaName || manualServiceArea) && (
                  <div>
                    <Label className='text-muted-foreground'>{t('serviceDetail.details.serviceArea')}</Label>
                    <div className='mt-1'>
                      {manualServiceArea ? (
                        <>
                          {manualServiceArea}
                          <Badge variant='outline' className='ml-2 text-xs bg-warning/10 border-warning text-warning-foreground'>
                            {t('serviceDetail.details.manual')}
                          </Badge>
                        </>
                      ) : (
                        serviceAreaName
                      )}
                    </div>
                  </div>
                )}
                {duty.specialty && (
                  <div>
                    <Label className='text-muted-foreground'>{t('serviceDetail.details.specialty')}</Label>
                    <p className='mt-1'>{duty.specialty}</p>
                  </div>
                )}
                {duty.hasNotfallscheinbeteiligung && (
                  <div>
                    <Label className='text-muted-foreground'>{t('serviceDetail.details.emergencyParticipation')}</Label>
                    <p className='mt-1'>{t('serviceDetail.details.amountPerPatient', { amount: duty.notfallscheinbeteiligungRate })}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className='text-muted-foreground'>{t('serviceDetail.details.description')}</Label>
                <p className='mt-1 whitespace-pre-wrap'>{duty.description || t('serviceDetail.details.noDescription')}</p>
              </div>

              {duty.notes && (
                <div>
                  <Label className='text-muted-foreground'>{t('serviceDetail.details.publicNotes')}</Label>
                  <p className='mt-1 whitespace-pre-wrap'>{duty.notes}</p>
                </div>
              )}

              {duty.privateNotes && (
                <div>
                  <Label className='text-muted-foreground'>{t('serviceDetail.details.privateNotes')}</Label>
                  <p className='mt-1 whitespace-pre-wrap'>{duty.privateNotes}</p>
                </div>
              )}

              {duty.requirements && (
                <div>
                  <Label className='text-muted-foreground'>{t('serviceDetail.details.requirements')}</Label>
                  <p className='mt-1 whitespace-pre-wrap'>{duty.requirements}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value='applications'>
          <Card>
            <CardHeader>
              <CardTitle>{t('serviceDetail.applications.title')}</CardTitle>
              <CardDescription>{t('serviceDetail.applications.description')}</CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              {applications.length === 0 ? (
                <div className='py-12 text-center'>
                  <Icons.Inbox className='size-12 text-muted-foreground mx-auto mb-3' />
                  <p className='text-muted-foreground text-sm'>{t('serviceDetail.applications.empty')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceDetail.applications.columns.applicant')}</TableHead>
                      <TableHead>{t('serviceDetail.applications.columns.status')}</TableHead>
                      <TableHead>{t('serviceDetail.applications.columns.appliedAt')}</TableHead>
                      <TableHead>{t('serviceDetail.applications.columns.proposedCompensation')}</TableHead>
                      <TableHead>{t('serviceDetail.applications.columns.message')}</TableHead>
                      <TableHead className='text-right'>{t('serviceDetail.applications.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => {
                      const canEditCompensation = canOperatorEditApplicationCompensation({
                        application: app,
                        dutyAssigneeId: duty.assigneeId,
                        payments,
                      });
                      const canRemove = canOperatorRemoveApplication({
                        application: app,
                        dutyAssigneeId: duty.assigneeId,
                        payments,
                      });

                      return (
                        <TableRow key={app._id}>
                          <TableCell>
                            <div className='flex flex-col'>
                              <span className='font-medium'>{app.applicantName}</span>
                              <span className='text-sm text-muted-foreground'>{app.applicantEmail}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={app.status === 'accepted' ? 'default' : app.status === 'rejected' ? 'destructive' : 'secondary'}>
                              {app.status === 'pending_verification' && t('serviceDetail.applications.statuses.pendingVerification')}
                              {app.status === 'pending' && t('serviceDetail.applications.statuses.pending')}
                              {app.status === 'accepted' && t('serviceDetail.applications.statuses.accepted')}
                              {app.status === 'rejected' && t('serviceDetail.applications.statuses.rejected')}
                              {app.status === 'expired' && t('serviceDetail.applications.statuses.expired')}
                              {app.status === 'withdrawn' && t('serviceDetail.applications.statuses.withdrawn')}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(app.appliedAt, i18n.language)}</TableCell>
                          <TableCell>{app.proposedCompensation ? `${app.proposedCompensation}€/h` : t('serviceDetail.common.dash')}</TableCell>
                          <TableCell className='max-w-xs truncate'>{app.message || t('serviceDetail.common.dash')}</TableCell>
                          <TableCell className='text-right'>
                            {canEditCompensation || canRemove ? (
                              <div className='flex justify-end gap-2'>
                                {canEditCompensation && (
                                  <Button variant='ghost' size='sm' data-testid={`edit-application-compensation-${app._id}`} onClick={() => setApplicationToEdit(app)}>
                                    <Icons.Pencil className='size-4' />
                                    <span>{t('serviceDetail.applications.actions.editCompensation')}</span>
                                  </Button>
                                )}
                                {canRemove && (
                                  <Button variant='ghost' size='sm' className='text-destructive hover:text-destructive' data-testid={`remove-application-${app._id}`} onClick={() => setApplicationToRemove(app)}>
                                    <Icons.Trash className='size-4' />
                                    <span>{t('serviceDetail.applications.actions.remove')}</span>
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className='text-sm text-muted-foreground'>{t('serviceDetail.common.dash')}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Report Tab */}
        <TabsContent value='activity-report' className='space-y-6'>
          {duty.status === 'performed' && duty.activityReport && !duty.activityReportConfirmedAt && (
            <OperatorConfirmServiceCard
              dutyId={duty._id}
              hasNotfallscheinbeteiligung={duty.hasNotfallscheinbeteiligung ?? false}
              kassenpatienten={duty.activityReport.kassenpatienten}
              uploadedDocuments={documents.length}
            />
          )}
          {duty.activityReport ? (
            <ActivityReportEditCard
              dutyId={duty._id}
              activityReport={duty.activityReport}
              hasNotfallscheinbeteiligung={duty.hasNotfallscheinbeteiligung ?? false}
              notfallscheinbeteiligungRate={duty.notfallscheinbeteiligungRate}
              isConfirmed={!!duty.activityReportConfirmedAt}
              confirmedAt={duty.activityReportConfirmedAt}
            />
          ) : (
            <Card>
              <CardContent className='py-12 text-center'>
                <Icons.FileText className='size-12 text-muted-foreground mx-auto mb-3' />
                <p className='text-muted-foreground text-sm'>{t('serviceDetail.activityReport.emptyTitle')}</p>
                <p className='text-muted-foreground text-xs mt-1'>{t('serviceDetail.activityReport.emptyDescription')}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value='documents' className='space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>{t('serviceDetail.documents.title')}</CardTitle>
              <CardDescription>{t('serviceDetail.documents.description')}</CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              {documents.length === 0 && serviceDetails.workInvoices.length === 0 && serviceDetails.platformInvoices.length === 0 && !serviceDetails.diensttauschFormular ? (
                <div className='py-12 text-center'>
                  <Icons.FileText className='size-12 text-muted-foreground mx-auto mb-3' />
                  <p className='text-muted-foreground text-sm'>{t('serviceDetail.documents.empty')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceDetail.documents.columns.filename')}</TableHead>
                      <TableHead>{t('serviceDetail.documents.columns.type')}</TableHead>
                      <TableHead>{t('serviceDetail.documents.columns.status')}</TableHead>
                      <TableHead>{t('serviceDetail.documents.columns.createdAt')}</TableHead>
                      <TableHead>{t('serviceDetail.documents.columns.patientCount')}</TableHead>
                      <TableHead>{t('serviceDetail.documents.columns.amount')}</TableHead>
                      <TableHead>{t('serviceDetail.documents.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Document Rows */}
                    {documents.map((doc) => (
                      <TableRow key={doc._id}>
                        <TableCell className='font-medium'>{doc.filename}</TableCell>
                        <TableCell>
                          <Badge variant='outline'>
                            {doc.documentType === 'schein' && t('serviceDetail.documents.types.schein')}
                            {doc.documentType === 'image' && t('serviceDetail.documents.types.image')}
                            {doc.documentType === 'other' && t('serviceDetail.documents.types.other')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={doc.status === 'approved' ? 'default' : doc.status === 'rejected' ? 'destructive' : 'secondary'}>
                            {doc.status === 'submitted' && t('serviceDetail.documents.statuses.submitted')}
                            {doc.status === 'approved' && t('serviceDetail.documents.statuses.approved')}
                            {doc.status === 'rejected' && t('serviceDetail.documents.statuses.rejected')}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(doc.submittedAt, i18n.language)}</TableCell>
                        <TableCell>{doc.patientCount || t('serviceDetail.common.dash')}</TableCell>
                        <TableCell>{doc.notfallscheinbeteiligungAmount ? formatCurrency(doc.notfallscheinbeteiligungAmount) : t('serviceDetail.common.dash')}</TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={async () => {
                              try {
                                const url = await convex.query(api.app.storage.queries.getStorageUrl, {
                                  storageId: doc.fileId,
                                });
                                if (url) {
                                  window.open(url, '_blank');
                                } else {
                                  toast.error(t('serviceDetail.toasts.downloadUrlError'));
                                }
                              } catch (error) {
                                console.error('Download error:', error);
                                toast.error(t('serviceDetail.toasts.downloadError'));
                              }
                            }}
                          >
                            <Icons.Download className='size-4 mr-2' />
                            {t('serviceDetail.common.download')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Work Invoices Section */}
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>{t('serviceDetail.workInvoices.title')}</CardTitle>
              <CardDescription>{t('serviceDetail.workInvoices.description')}</CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              {serviceDetails.workInvoices.length === 0 ? (
                <div className='py-8 text-center'>
                  <Icons.FileText className='size-8 text-muted-foreground mx-auto mb-2' />
                  <p className='text-muted-foreground text-sm'>{t('serviceDetail.workInvoices.empty')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceDetail.workInvoices.columns.invoiceNumber')}</TableHead>
                      <TableHead>{t('serviceDetail.workInvoices.columns.status')}</TableHead>
                      <TableHead>{t('serviceDetail.workInvoices.columns.baseAmount')}</TableHead>
                      <TableHead>{t('serviceDetail.workInvoices.columns.emergencyAmount')}</TableHead>
                      <TableHead>{t('serviceDetail.workInvoices.columns.totalAmount')}</TableHead>
                      <TableHead>{t('serviceDetail.workInvoices.columns.createdAt')}</TableHead>
                      <TableHead>{t('serviceDetail.workInvoices.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {serviceDetails.workInvoices.map((invoice) => (
                      <TableRow key={invoice._id}>
                        <TableCell className='font-medium'>{invoice.invoiceNumber || t('serviceDetail.common.dash')}</TableCell>
                        <TableCell>
                          <Badge variant={invoice.status === 'paid' ? 'default' : invoice.status === 'superseded' ? 'destructive' : 'secondary'}>
                            {invoice.status === 'draft' && t('serviceDetail.workInvoices.statuses.draft')}
                            {invoice.status === 'issued' && t('serviceDetail.workInvoices.statuses.issued')}
                            {invoice.status === 'paid' && t('serviceDetail.workInvoices.statuses.paid')}
                            {invoice.status === 'superseded' && t('serviceDetail.workInvoices.statuses.superseded')}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(invoice.baseAmount)}</TableCell>
                        <TableCell>{invoice.notfallscheinbeteiligungAmount ? formatCurrency(invoice.notfallscheinbeteiligungAmount) : t('serviceDetail.common.dash')}</TableCell>
                        <TableCell className='font-medium'>{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell>{formatDate(invoice.issuedAt, i18n.language)}</TableCell>
                        <TableCell>
                          {invoice.storageId && (
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={async () => {
                                try {
                                  const url = await convex.query(api.app.storage.queries.getStorageUrl, {
                                    storageId: invoice.storageId!,
                                  });
                                  if (url) window.open(url, '_blank');
                                  else toast.error(t('serviceDetail.toasts.downloadUrlError'));
                                } catch {
                                  toast.error(t('serviceDetail.toasts.downloadError'));
                                }
                              }}
                            >
                              <Icons.Download className='size-4 mr-2' />
                              {t('serviceDetail.common.pdf')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Platform Invoices Section */}
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>{t('serviceDetail.platformInvoices.title')}</CardTitle>
              <CardDescription>{t('serviceDetail.platformInvoices.description')}</CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              {serviceDetails.platformInvoices.length === 0 ? (
                <div className='py-8 text-center'>
                  <Icons.Receipt className='size-8 text-muted-foreground mx-auto mb-2' />
                  <p className='text-muted-foreground text-sm'>{t('serviceDetail.platformInvoices.empty')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.invoiceNumber')}</TableHead>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.recipient')}</TableHead>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.status')}</TableHead>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.netAmount')}</TableHead>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.vatAmount')}</TableHead>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.totalAmount')}</TableHead>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.createdAt')}</TableHead>
                      <TableHead>{t('serviceDetail.platformInvoices.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {serviceDetails.platformInvoices.map((invoice) => {
                      const payment = paymentsById.get(invoice.paymentId);
                      const canRepair = payment?.canRepairInvoice === true;
                      return (
                      <TableRow key={invoice._id}>
                        <TableCell className='font-medium'>{invoice.invoiceNumber}</TableCell>
                        <TableCell>
                          <div className='flex flex-col'>
                            <span className='text-sm'>{invoice.recipientName}</span>
                            <span className='text-xs text-muted-foreground'>{invoice.recipientEmail}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={invoice.status === 'paid' ? 'default' : invoice.status === 'overdue' || invoice.status === 'cancelled' ? 'destructive' : 'secondary'}>
                            {invoice.status === 'draft' && t('serviceDetail.platformInvoices.statuses.draft')}
                            {invoice.status === 'sent' && t('serviceDetail.platformInvoices.statuses.sent')}
                            {invoice.status === 'paid' && t('serviceDetail.platformInvoices.statuses.paid')}
                            {invoice.status === 'overdue' && t('serviceDetail.platformInvoices.statuses.overdue')}
                            {invoice.status === 'cancelled' && t('serviceDetail.platformInvoices.statuses.cancelled')}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(invoice.netAmount)}</TableCell>
                        <TableCell>{formatCurrency(invoice.vatAmount)}</TableCell>
                        <TableCell className='font-medium'>{formatCurrency(invoice.total)}</TableCell>
                        <TableCell>{formatDate(invoice.issuedAt, i18n.language)}</TableCell>
                        <TableCell>
                          {invoice.pdfFileId && (
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={async () => {
                                try {
                                  const url = await convex.query(api.app.storage.queries.getStorageUrl, {
                                    storageId: invoice.pdfFileId!,
                                  });
                                  if (url) window.open(url, '_blank');
                                  else toast.error(t('serviceDetail.toasts.downloadUrlError'));
                                } catch {
                                  toast.error(t('serviceDetail.toasts.downloadError'));
                                }
                              }}
                            >
                              <Icons.Download className='size-4 mr-2' />
                              {t('serviceDetail.common.pdf')}
                            </Button>
                          )}
                          {canRepair && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() =>
                                setInvoiceRepairTarget({
                                  paymentId: invoice.paymentId,
                                  invoiceNumber: invoice.invoiceNumber,
                                  recipientName: invoice.recipientName,
                                })
                              }
                            >
                              {t('accounting.invoices.repair.action')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Diensttauschformular Section */}
          <OperatorDiensttauschFormularCard
            formular={serviceDetails.diensttauschFormular}
            draft={diensttauschDraft}
            onOpen={(draft, regenerate) => setDiensttauschDialog({ draft, regenerate })}
          />

          {/* Honorarvertrag Section */}
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <div>
                <CardTitle className='text-lg'>{t('serviceDetail.contracts.title')}</CardTitle>
                <CardDescription>{t('serviceDetail.contracts.description')}</CardDescription>
              </div>
              <Button variant='outline' size='sm' onClick={handleGenerateHonorarvertragClick} disabled={!assignee || isGeneratingHonorarvertrag}>
                {isGeneratingHonorarvertrag ? (
                  <>
                    <Icons.Loader2 className='size-4 mr-2 animate-spin' />
                    {t('serviceDetail.contracts.generating')}
                  </>
                ) : (
                  <>
                    <Icons.Plus className='size-4 mr-2' />
                    {(serviceDetails.honorarvertragContracts?.length ?? 0) > 0 ? t('serviceDetail.contracts.regenerate') : t('serviceDetail.contracts.create')}
                  </>
                )}
              </Button>
            </CardHeader>
            <CardContent className='p-0'>
              {(serviceDetails.honorarvertragContracts?.length ?? 0) === 0 ? (
                <div className='py-8 text-center'>
                  <Icons.FileText className='size-8 text-muted-foreground mx-auto mb-2' />
                  <p className='text-muted-foreground text-sm'>{t('serviceDetail.contracts.empty')}</p>
                  {!assignee && <p className='text-muted-foreground text-xs mt-1'>{t('serviceDetail.contracts.emptyDescriptionWithoutAssignee')}</p>}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceDetail.contracts.columns.filename')}</TableHead>
                      <TableHead>{t('serviceDetail.contracts.columns.status')}</TableHead>
                      <TableHead>{t('serviceDetail.contracts.columns.version')}</TableHead>
                      <TableHead>{t('serviceDetail.contracts.columns.provider')}</TableHead>
                      <TableHead>{t('serviceDetail.contracts.columns.substitute')}</TableHead>
                      <TableHead>{t('serviceDetail.contracts.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(serviceDetails.honorarvertragContracts ?? []).map((contract) => {
                      const statusBadge = getHonorarvertragStatusBadge(contract.status);
                      return (
                        <TableRow key={contract._id}>
                          <TableCell className='font-medium'>{contract.filename}</TableCell>
                          <TableCell>
                            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                          </TableCell>
                          <TableCell>{t('serviceDetail.contracts.versionLabel', { version: contract.version })}</TableCell>
                          <TableCell>
                            {contract.providerAcceptedAt ? (
                              <span className='text-sm text-primary'>
                                {'\u2713'} {formatDate(contract.providerAcceptedAt, i18n.language)}
                              </span>
                            ) : (
                              <span className='text-sm text-muted-foreground'>
                                {'\u25CB'} {t('serviceDetail.contracts.pending')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {contract.substituteAcceptedAt ? (
                              <span className='text-sm text-primary'>
                                {'\u2713'} {formatDate(contract.substituteAcceptedAt, i18n.language)}
                              </span>
                            ) : (
                              <span className='text-sm text-muted-foreground'>
                                {'\u25CB'} {t('serviceDetail.contracts.pending')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {contract.fileId && (
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={async () => {
                                  try {
                                    const url = await convex.query(api.app.storage.queries.getStorageUrl, {
                                      storageId: contract.fileId!,
                                    });
                                    if (url) window.open(url, '_blank');
                                    else toast.error(t('serviceDetail.toasts.downloadUrlError'));
                                  } catch {
                                    toast.error(t('serviceDetail.toasts.downloadError'));
                                  }
                                }}
                              >
                                <Icons.Download className='size-4 mr-2' />
                                {t('serviceDetail.common.download')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value='payments'>
          <Card>
            <CardHeader className='flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between'>
              <div className='space-y-1.5'>
                <CardTitle>{t('serviceDetail.payments.title')}</CardTitle>
                <CardDescription>{duty.status === 'pending_payment' ? t('serviceDetail.payments.convertToInvoiceHint') : t('serviceDetail.payments.description')}</CardDescription>
              </div>
              {duty.status === 'pending_payment' && (
                <Button variant='outline' size='sm' className='shrink-0' onClick={() => handleConvertToInvoice(duty._id)} disabled={isConvertingToInvoice} aria-busy={isConvertingToInvoice}>
                  {isConvertingToInvoice ? <Icons.Loader2 className='size-4 mr-2 animate-spin' aria-hidden='true' /> : <Icons.FileText className='size-4 mr-2' aria-hidden='true' />}
                  {isConvertingToInvoice ? t('serviceDetail.payments.converting') : t('serviceDetail.payments.convertToInvoice')}
                </Button>
              )}
            </CardHeader>
            <CardContent className='p-0'>
              {payments.length === 0 ? (
                <div className='py-12 text-center'>
                  <Icons.CreditCard className='size-12 text-muted-foreground mx-auto mb-3' />
                  <p className='text-muted-foreground text-sm'>{t('serviceDetail.payments.empty')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceDetail.payments.columns.type')}</TableHead>
                      <TableHead>{t('serviceDetail.payments.columns.amount')}</TableHead>
                      <TableHead>{t('serviceDetail.payments.columns.status')}</TableHead>
                      <TableHead>{t('serviceDetail.payments.columns.mollieId')}</TableHead>
                      <TableHead>{t('serviceDetail.payments.columns.createdAt')}</TableHead>
                      <TableHead>{t('serviceDetail.payments.columns.paidAt')}</TableHead>
                      <TableHead>{t('serviceDetail.payments.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => {
                      const invoice = invoicesByPaymentId.get(payment._id);
                      const canRepair = payment.canRepairInvoice;
                      return (
                      <TableRow key={payment._id}>
                        <TableCell>
                          <Badge variant='outline'>
                            {payment.paymentType === 'provider_platform_fee' && t('serviceDetail.payments.types.providerPlatformFee')}
                            {payment.paymentType === 'substitute_platform_fee' && t('serviceDetail.payments.types.substitutePlatformFee')}
                            {payment.paymentType === 'work_payment' && t('serviceDetail.payments.types.workPayment')}
                            {payment.paymentType === 'invoice_payment' && t('serviceDetail.payments.types.invoicePayment')}
                            {!payment.paymentType && t('serviceDetail.payments.types.other')}
                          </Badge>
                        </TableCell>
                        <TableCell className='font-medium'>{formatCurrency(payment.amount)}</TableCell>
                        <TableCell>
                          <Badge variant={payment.status === 'succeeded' ? 'default' : payment.status === 'failed' || payment.status === 'canceled' ? 'destructive' : 'secondary'}>
                            {payment.status === 'pending' && t('serviceDetail.payments.statuses.pending')}
                            {payment.status === 'succeeded' && t('serviceDetail.payments.statuses.succeeded')}
                            {payment.status === 'failed' && t('serviceDetail.payments.statuses.failed')}
                            {payment.status === 'expired' && t('serviceDetail.payments.statuses.expired')}
                            {payment.status === 'canceled' && t('serviceDetail.payments.statuses.canceled')}
                            {payment.status === 'refunded' && t('serviceDetail.payments.statuses.refunded')}
                          </Badge>
                        </TableCell>
                        <TableCell className='font-mono text-sm'>{payment.molliePaymentId}</TableCell>
                        <TableCell>{payment.createdAt ? formatDate(payment.createdAt, i18n.language) : t('serviceDetail.common.dash')}</TableCell>
                        <TableCell>{payment.paidAt ? formatDate(payment.paidAt, i18n.language) : t('serviceDetail.common.dash')}</TableCell>
                        <TableCell className='space-x-2 whitespace-nowrap'>
                          {payment.canRepairBookingFinalization && (
                            <Button
                              size='sm'
                              title={t('serviceDetail.payments.repairBookingHint')}
                              disabled={repairingBookingPaymentId !== null}
                              onClick={() => void handleRepairBookingFinalization(payment._id)}
                            >
                              {repairingBookingPaymentId === payment._id && <Icons.Loader2 className='size-4 animate-spin' />}
                              {t('serviceDetail.payments.repairBooking')}
                            </Button>
                          )}
                          {canRepair && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() =>
                                setInvoiceRepairTarget({
                                  paymentId: payment._id,
                                  ...(invoice?.invoiceNumber !== undefined && { invoiceNumber: invoice.invoiceNumber }),
                                  ...(invoice?.recipientName !== undefined && { recipientName: invoice.recipientName }),
                                })
                              }
                            >
                              {t('accounting.invoices.repair.action')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value='audit'>
          <Card>
            <CardHeader>
              <CardTitle>{t('serviceDetail.audit.title')}</CardTitle>
              <CardDescription>{t('serviceDetail.audit.description')}</CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              {audits.length === 0 ? (
                <div className='py-12 text-center'>
                  <Icons.History className='size-12 text-muted-foreground mx-auto mb-3' />
                  <p className='text-muted-foreground text-sm'>{t('serviceDetail.audit.empty')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceDetail.audit.columns.action')}</TableHead>
                      <TableHead>{t('serviceDetail.audit.columns.operator')}</TableHead>
                      <TableHead>{t('serviceDetail.audit.columns.reason')}</TableHead>
                      <TableHead>{t('serviceDetail.audit.columns.timestamp')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audits.map((audit) => (
                      <TableRow key={audit._id}>
                        <TableCell>
                          <Badge variant='outline'>{audit.action}</Badge>
                        </TableCell>
                        <TableCell>{audit.actorName}</TableCell>
                        <TableCell className='max-w-md truncate'>{audit.reason || t('serviceDetail.common.dash')}</TableCell>
                        <TableCell>{formatDate(audit.createdAt, i18n.language)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {duty && <StatusChangeDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen} currentStatus={duty.status} serviceId={duty._id} />}
      {duty && deletionReviewDialog && <DeletionRequestReviewDialog mode={deletionReviewDialog} open={deletionReviewDialog !== null} onOpenChange={(open) => setDeletionReviewDialog(open ? deletionReviewDialog : null)} serviceId={duty._id} />}
      {duty && (
        <OverrideApplicationCompensationDialog
          application={applicationToEdit}
          duty={duty}
          open={applicationToEdit !== null}
          onOpenChange={(open) => {
            if (!open) {
              setApplicationToEdit(null);
            }
          }}
        />
      )}
      <RemoveApplicationDialog
        application={applicationToRemove}
        open={applicationToRemove !== null}
        onOpenChange={(open) => {
          if (!open) {
            setApplicationToRemove(null);
          }
        }}
      />
      <RepairPlatformInvoiceDialog
        target={invoiceRepairTarget}
        onOpenChange={(open) => {
          if (!open) setInvoiceRepairTarget(null);
        }}
        onRepair={handleRepairPlatformFeeInvoice}
      />
      {diensttauschDialog && (
        <DiensttauschFormularDialog
          dutyId={duty._id}
          draft={diensttauschDialog.draft}
          regenerate={diensttauschDialog.regenerate}
          open={true}
          onOpenChange={(open) => {
            if (!open) setDiensttauschDialog(null);
          }}
        />
      )}
      {duty && <EditServiceDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} service={duty} />}
      {duty && <SendEmailDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} serviceId={duty._id} serviceTitle={duty.title} />}
      {duty && (
        <AssignSubstituteDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          serviceId={duty._id}
          currentAssignee={
            assignee
              ? {
                  _id: assignee._id,
                  name: assignee.firstName && assignee.lastName ? `${assignee.firstName} ${assignee.lastName}` : assignee.email,
                  email: assignee.email,
                }
              : null
          }
          applications={applications || []}
          onSuccess={() => {
            // The useQuery hook will automatically refetch the service data
            // No manual refresh needed due to Convex real-time subscriptions
          }}
        />
      )}
      {/* Regenerate Contract Confirmation Dialog */}
      {serviceDetails && (serviceDetails.honorarvertragContracts?.length ?? 0) > 0 && (
        <RegenerateContractDialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen} contract={serviceDetails.honorarvertragContracts[0]} onConfirm={handleGenerateHonorarvertrag} isGenerating={isGeneratingHonorarvertrag} />
      )}
    </div>
  );
};

// Error codes thrown by operatorRepairProviderBookingFinalization, mapped to translation keys
const BOOKING_REPAIR_ERROR_KEYS: Record<string, string> = {
  BOOKING_REPAIR_NOT_ELIGIBLE: 'notEligible',
  BOOKING_REPAIR_ALREADY_BOOKED: 'alreadyBooked',
  BOOKING_REPAIR_CONFLICT: 'conflict',
};

export const Route = createFileRoute('/_protected/operators/services/$serviceId')({
  component: OperatorServiceDetailsPage,
});
